import type {
  AdminUser,
  AnswerUnansweredRequest,
  CaseDetail,
  CaseListItem,
  ChatRequest,
  ConversationDetail,
  ConversationSummary,
  CreateCaseRequest,
  CreateUserRequest,
  IngestReportSummary,
  KnowledgeDoc,
  KnowledgeDocSummary,
  KnowledgeMutationResult,
  LoginResponse,
  SaveKnowledgeDocRequest,
  ServerEvent,
  SettingKey,
  SettingsResponse,
  UnansweredQuestion,
  UnansweredStatus,
  UpdateCaseRequest,
  UpdateSettingsRequest,
  UserCredential,
  UserRole,
} from "@maimu/contracts";

import { endSession, token } from "./session.js";
import { readServerEvents } from "./sse.js";

export interface HealthInfo {
  status: string;
  database: boolean;
  /** Null when the corpus could not be counted (database down). */
  knowledge: { sources: number; chunks: number } | null;
  llm: {
    configured: boolean;
    model: string;
    visionModel: string;
    maxOutputTokens: number;
    temperature: number;
    reasoningEffort: string;
  };
  tools: { jahk: string; directo: string; shopify: string; maxRounds: number; writeToolsEnabled: boolean };
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

function authHeaders(): Record<string, string> {
  return token.value ? { Authorization: `Bearer ${token.value}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...init.headers },
  });

  if (response.status === 401) {
    endSession();
    throw new ApiError("Sessioon on aegunud. Logi uuesti sisse.", 401);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? "Midagi läks päringuga valesti.", response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  login(username: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  health(): Promise<HealthInfo> {
    return request<HealthInfo>("/health");
  },

  conversations(): Promise<ConversationSummary[]> {
    return request<ConversationSummary[]>("/api/conversations");
  },

  conversation(id: string): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/api/conversations/${id}`);
  },

  deleteConversation(id: string): Promise<void> {
    return request<void>(`/api/conversations/${id}`, { method: "DELETE" });
  },

  // ── Learning loop (admin) ──────────────────────────────────────────────────
  unanswered(status: UnansweredStatus | "all" = "open"): Promise<UnansweredQuestion[]> {
    return request<UnansweredQuestion[]>(`/api/unanswered?status=${status}`);
  },

  unansweredStats(): Promise<{ open: number; answered: number }> {
    return request<{ open: number; answered: number }>("/api/unanswered/stats");
  },

  answerUnanswered(id: string, answer: string): Promise<UnansweredQuestion> {
    return request<UnansweredQuestion>(`/api/unanswered/${id}/answer`, {
      method: "POST",
      body: JSON.stringify({ answer } satisfies AnswerUnansweredRequest),
    });
  },

  dismissUnanswered(id: string): Promise<void> {
    return request<void>(`/api/unanswered/${id}/dismiss`, { method: "POST" });
  },

  // ── Toimikud (D21) ─────────────────────────────────────────────────────────
  cases(): Promise<CaseListItem[]> {
    return request<CaseListItem[]>("/api/cases");
  },

  case(id: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}`);
  },

  createCase(body: CreateCaseRequest): Promise<CaseDetail> {
    return request<CaseDetail>("/api/cases", { method: "POST", body: JSON.stringify(body) });
  },

  updateCase(id: string, body: UpdateCaseRequest): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  },

  addCaseNote(id: string, body: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}/notes`, { method: "POST", body: JSON.stringify({ body }) });
  },

  addCaseMember(id: string, username: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}/members`, { method: "POST", body: JSON.stringify({ username }) });
  },

  removeCaseMember(id: string, username: string): Promise<CaseDetail | void> {
    return request<CaseDetail | void>(`/api/cases/${id}/members/${encodeURIComponent(username)}`, { method: "DELETE" });
  },

  joinCase(id: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}/join`, { method: "POST" });
  },

  caseConversation(id: string, conversationId: string): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/api/cases/${id}/conversations/${conversationId}`);
  },

  attachConversationToCase(id: string, conversationId: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}/conversations`, {
      method: "POST",
      body: JSON.stringify({ conversationId }),
    });
  },

  detachConversationFromCase(caseId: string, conversationId: string): Promise<void> {
    return request<void>(`/api/cases/${caseId}/conversations/${conversationId}`, { method: "DELETE" });
  },

  addCaseFiles(id: string, files: { name: string; dataUrl: string }[]): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${id}/files`, { method: "POST", body: JSON.stringify({ files }) });
  },

  // ── Admin panel (D18) ──────────────────────────────────────────────────────
  adminSettings(): Promise<SettingsResponse> {
    return request<SettingsResponse>("/api/admin/settings");
  },

  saveAdminSettings(patch: UpdateSettingsRequest): Promise<SettingsResponse> {
    return request<SettingsResponse>("/api/admin/settings", { method: "PUT", body: JSON.stringify(patch) });
  },

  resetAdminSetting(key: SettingKey): Promise<SettingsResponse> {
    return request<SettingsResponse>(`/api/admin/settings/${key}`, { method: "DELETE" });
  },

  knowledgeDocs(): Promise<KnowledgeDocSummary[]> {
    return request<KnowledgeDocSummary[]>("/api/admin/knowledge");
  },

  knowledgeDoc(path: string): Promise<KnowledgeDoc> {
    return request<KnowledgeDoc>(`/api/admin/knowledge/doc?path=${encodeURIComponent(path)}`);
  },

  saveKnowledgeDoc(doc: SaveKnowledgeDocRequest): Promise<KnowledgeMutationResult> {
    return request<KnowledgeMutationResult>("/api/admin/knowledge/doc", {
      method: "POST",
      body: JSON.stringify(doc),
    });
  },

  deleteKnowledgeDoc(path: string): Promise<KnowledgeMutationResult> {
    return request<KnowledgeMutationResult>(`/api/admin/knowledge/doc?path=${encodeURIComponent(path)}`, {
      method: "DELETE",
    });
  },

  adminUsers(): Promise<AdminUser[]> {
    return request<AdminUser[]>("/api/admin/users");
  },

  createAdminUser(body: CreateUserRequest): Promise<UserCredential> {
    return request<UserCredential>("/api/admin/users", { method: "POST", body: JSON.stringify(body) });
  },

  resetAdminUserPassword(id: string): Promise<UserCredential> {
    return request<UserCredential>(`/api/admin/users/${id}/password`, { method: "POST" });
  },

  setAdminUserRole(id: string, role: UserRole): Promise<AdminUser> {
    return request<AdminUser>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify({ role }) });
  },

  deleteAdminUser(id: string): Promise<void> {
    return request<void>(`/api/admin/users/${id}`, { method: "DELETE" });
  },

  reingestKnowledge(): Promise<IngestReportSummary> {
    return request<IngestReportSummary>("/api/admin/knowledge/reingest", { method: "POST" });
  },

  /** Streams one answer. `signal` lets the user abort a long tool round. */
  async *chat(body: ChatRequest, signal?: AbortSignal): AsyncGenerator<ServerEvent> {
    const response = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...authHeaders() },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

    if (response.status === 401) {
      endSession();
      throw new ApiError("Sessioon on aegunud. Logi uuesti sisse.", 401);
    }

    if (!response.ok) {
      const error = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(error?.error ?? "Assistent ei vastanud.", response.status);
    }

    yield* readServerEvents(response);
  },
};

export { ApiError };
